import * as NextSkyTheme from "@NextSkyTheme/global";
import { LazyLoader } from "@NextSkyTheme/lazy-load";
import { notifier } from "@NextSkyTheme/notification";
import { eventModal } from "@NextSkyTheme/modal";

const BundleUtils = {
  getWrapper(el) {
    return el.closest("build-your-routine");
  },
  getSectionId(el) {
    return (
      el?.dataset?.sectionId ||
      el.closest("[data-section-id]")?.dataset.sectionId
    );
  },
  getBundleItems(wrapper) {
    return wrapper.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id]"
    );
  },
  getBundledVariantIds(wrapper) {
    return Array.from(BundleUtils.getBundleItems(wrapper)).map((item) =>
      item.getAttribute("data-variant-id")
    );
  },
  clearContainer(container) {
    container.removeAttribute("data-variant-id");
    container.removeAttribute("data-quantity");
    container.removeAttribute("data-product-handle");

    const mediaContainer = container.querySelector(
      "[data-product-bundle-variant-media]"
    );
    if (mediaContainer) {
      mediaContainer.innerHTML = "";
      mediaContainer.style.setProperty("--aspect-ratio", "3/4");
    }

    const contentContainer = container.querySelector(
      "[data-product-bundle-variant-content]"
    );
    if (contentContainer) {
      contentContainer.innerHTML = `
              <span class="skeleton-product__info skeleton-1 h-custom bg-secondary rounded-10 max-w-custom-all" style="--max-width: 100%;"></span>
              <span class="skeleton-product__info skeleton-2 h-custom bg-secondary rounded-10 max-w-custom-all" style="--max-width: 6rem;"></span>
              <span class="skeleton-product__info skeleton-3 h-custom bg-secondary rounded-10 max-w-custom-all" style="--max-width: 12rem"></span>
            `;
    }

    const actionContainer = container.querySelector(".bundle-action");
    if (actionContainer) {
      actionContainer.innerHTML = "";
    }
  },
  saveBundleToStorage(wrapper, sectionId) {
    try {
      const items = [];
      BundleUtils.getBundleItems(wrapper).forEach((item) => {
        const variantId = item.getAttribute("data-variant-id");
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        const productHandle = item.getAttribute("data-product-handle");
        if (variantId && productHandle)
          items.push({ variantId, quantity, productHandle });
      });
      const key = `bundle-items-${sectionId}`;
      if (items.length > 0) localStorage.setItem(key, JSON.stringify(items));
      else localStorage.removeItem(key);
    } catch (e) {
      console.error("Error saving bundle to storage:", e);
    }
  },
  updateProductFormButtons(wrapper, opts = {}) {
    const { disableAll = false } = opts;
    const bundledVariantIds = BundleUtils.getBundledVariantIds(wrapper);
    const productForms = wrapper.querySelectorAll("product-form-bundle");
    productForms.forEach((form) => {
      const variantIdInput = form.querySelector('input[name="id"]');
      const submitButton = form.querySelector('button[type="submit"]');
      if (!variantIdInput || !submitButton) return;
      const variantId = variantIdInput.value;
      if (bundledVariantIds.includes(variantId) || disableAll) {
        submitButton.classList.add("disabled");
        submitButton.setAttribute("disabled", true);
        submitButton.setAttribute("aria-disabled", true);
        submitButton.textContent =
          window.cartStrings?.added_to_bundle || "Added to Bundle";
      } else {
        submitButton.classList.remove("disabled");
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-disabled");
        submitButton.textContent =
          window.cartStrings?.add_to_bundle || "Add to Bundle";
      }
    });
  },
  setBundleTotal(sectionId, amount) {
    const totalElement = document.querySelector(
      `.bundle-total-price-${sectionId}`
    );
    if (!totalElement) return;
    totalElement.textContent = NextSkyTheme.formatMoney(
      amount,
      themeGlobalVariables.settings.money_format
    );
  },
  setBundleDiscountInfo(sectionId, discountInfo) {
    const discountElement = document.querySelector(
      `.bundle-discount-info-${sectionId}`
    );
    if (!discountElement) return;
    if (discountInfo && discountInfo.title) {
      discountElement.textContent = discountInfo.title;
      discountElement.style.display = "block";
    } else {
      discountElement.style.display = "none";
    }
  },
  computeTotalPrice(wrapper, ds) {
    try {
      let total = 0;
      let discountTotal = 0;
      const items = wrapper.querySelectorAll(
        "[data-product-bundle-variant][data-variant-id]"
      );

      // Compter soit les produits uniques, soit les quantités totales
      const countQuantities = ds?.countQuantities === "true";
      let itemCount = 0;
      if (countQuantities) {
        items.forEach((item) => {
          const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
          itemCount += quantity;
        });
      } else {
        itemCount = items.length;
      }

      // Calculer le total sans remise
      items.forEach((item) => {
        const priceElement = item.querySelector(".product__price");
        const price = parseFloat(priceElement?.dataset.price || 0);
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        if (price) {
          total += price * quantity;
        }
      });

      // Vérifier d'abord les remises conditionnelles (priorité)
      // Note: Les attributs data-* avec chiffres à la fin ne sont pas correctement convertis en camelCase
      // On doit utiliser getAttribute() directement sur le wrapper
      const enableDiscount1 =
        wrapper.getAttribute("data-enable-discount-1") === "true";
      const discountTitle1 =
        wrapper.getAttribute("data-discount-title-1") || "";
      const discountCode1 = wrapper.getAttribute("data-discount-code-1") || "";
      const discountType1 =
        wrapper.getAttribute("data-discount-type-1") || "percentage";
      const discountValue1 = enableDiscount1
        ? Number(wrapper.getAttribute("data-discount-value-1")) || 0
        : 0;
      const discountMinimum1 = enableDiscount1
        ? parseInt(wrapper.getAttribute("data-discount-minimum-1")) || 0
        : 0;

      const enableDiscount2 =
        wrapper.getAttribute("data-enable-discount-2") === "true";
      const discountTitle2 =
        wrapper.getAttribute("data-discount-title-2") || "";
      const discountCode2 = wrapper.getAttribute("data-discount-code-2") || "";
      const discountType2 =
        wrapper.getAttribute("data-discount-type-2") || "percentage";
      const discountValue2 = enableDiscount2
        ? Number(wrapper.getAttribute("data-discount-value-2")) || 0
        : 0;
      const discountMinimum2 = enableDiscount2
        ? parseInt(wrapper.getAttribute("data-discount-minimum-2")) || 0
        : 0;

      let appliedDiscount = null;

      // Vérifier la remise niveau 2 (priorité la plus haute)
      if (
        enableDiscount2 &&
        discountTitle2 &&
        itemCount >= discountMinimum2 &&
        discountValue2 > 0
      ) {
        if (discountType2 === "percentage") {
          // Le total est en centimes, calculer la remise en centimes
          discountTotal = Math.round((total * discountValue2) / 100);
        } else {
          // Montant fixe (en centimes)
          discountTotal = discountValue2 * 100;
        }
        appliedDiscount = {
          title: discountTitle2,
          code: discountCode2,
          type: discountType2,
          value: discountValue2,
        };
      }
      // Sinon vérifier la remise niveau 1
      else if (
        enableDiscount1 &&
        discountTitle1 &&
        itemCount >= discountMinimum1 &&
        discountValue1 > 0
      ) {
        if (discountType1 === "percentage") {
          // Le total est en centimes, calculer la remise en centimes
          discountTotal = Math.round((total * discountValue1) / 100);
        } else {
          // Montant fixe (en centimes)
          discountTotal = discountValue1 * 100;
        }
        appliedDiscount = {
          title: discountTitle1,
          code: discountCode1,
          type: discountType1,
          value: discountValue1,
        };
      }
      // Sinon utiliser le système de remise existant (remise par défaut)
      // La remise par défaut s'applique même si les remises conditionnelles sont activées
      // mais que leurs conditions ne sont pas remplies
      if (!appliedDiscount) {
        const discountTitle = ds?.discountTitle;
        const discountValue = ds?.discountValue;
        const valuePercentage = Number(ds?.valuePercentage);
        const minimum = parseInt(ds?.minimum || 0);

        if (itemCount >= minimum && discountTitle) {
          if (discountValue === "percentage" && valuePercentage) {
            items.forEach((item) => {
              const priceElement = item.querySelector(".product__price");
              const price = parseFloat(priceElement?.dataset.price || 0);
              const quantity =
                parseInt(item.getAttribute("data-quantity")) || 1;
              if (price) {
                const discountAmount = Math.floor(
                  (price * valuePercentage) / 100
                );
                discountTotal += discountAmount * quantity;
              }
            });
            appliedDiscount = {
              title: discountTitle,
              code: "",
              type: "percentage",
              value: valuePercentage,
            };
          } else if (discountValue === "fixed_amount") {
            const valueFixedAmount = Number(ds?.valueFixedAmount);
            const oncePerOrder = ds?.oncePerOrder === "true";

            if (valueFixedAmount) {
              let totalPriceSale = 0;
              if (oncePerOrder) {
                items.forEach((item) => {
                  const priceElement = item.querySelector(".product__price");
                  const price = parseFloat(priceElement?.dataset.price || 0);
                  const quantity =
                    parseInt(item.getAttribute("data-quantity")) || 1;
                  const priceItem = price * quantity;
                  if (total > 0) {
                    totalPriceSale +=
                      (priceItem / total) * (valueFixedAmount * 100);
                  }
                });
                discountTotal = totalPriceSale;
              } else {
                items.forEach((item) => {
                  const quantity =
                    parseInt(item.getAttribute("data-quantity")) || 1;
                  totalPriceSale += quantity * (valueFixedAmount * 100);
                });
                discountTotal = totalPriceSale;
              }
              appliedDiscount = {
                title: discountTitle,
                code: "",
                type: "fixed_amount",
                value: valueFixedAmount,
              };
            }
          }
        }
      }

      // Appliquer la remise calculée
      if (appliedDiscount && discountTotal > 0) {
        total = total - discountTotal;
        // Stocker le code de réduction pour l'application au panier
        if (appliedDiscount.code) {
          wrapper.dataset.activeDiscountCode = appliedDiscount.code;
        } else {
          delete wrapper.dataset.activeDiscountCode;
        }
        // Stocker les informations de remise pour l'affichage
        wrapper.dataset.activeDiscountTitle = appliedDiscount.title;
        wrapper.dataset.activeDiscountAmount = discountTotal.toString();
      } else {
        // Supprimer les informations de remise si aucune remise n'est active
        delete wrapper.dataset.activeDiscountTitle;
        delete wrapper.dataset.activeDiscountAmount;
        delete wrapper.dataset.activeDiscountCode;
      }

      return total;
    } catch (e) {
      console.error("Error computing total price:", e);
      return 0;
    }
  },
};

if (!customElements.get("product-form-bundle")) {
  customElements.define(
    "product-form-bundle",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.sectionId = this.dataset.sectionId;
        this.bundle = this.closest("build-your-routine");
        this.localStorageKey = `bundle-items-${this.sectionId}`;
        this.localDiscountKey = "bundle-discount";
        this.localSectionKey = `bundle-section`;
        this.localMinMaxKey = `bundle-min-max-${this.sectionId}`;
        this.isLoadingFromStorage = false;
        this.maximum = parseInt(this.dataset.maximum) || 0;
        this.minimum = parseInt(this.dataset.minimum) || 0;
        this.bundleSubmitButton = this.bundle.querySelector(
          "button-submit-bundle"
        );

        // Écouter les changements pour mettre à jour les remises
        document.addEventListener("bundle:item-changed", () => {
          this.updateDiscount();
          this.updateBundleTotal();
        });
        document.addEventListener("bundle:quantity-changed", () => {
          this.updateDiscount();
          this.updateBundleTotal();
        });

        const isFirstForm =
          this.bundle.querySelector("product-form-bundle") === this;
        if (isFirstForm) {
          setTimeout(() => {
            this.loadBundleFromStorage();
          }, 50);
        }
      }

      onSubmitHandler(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.submitButton.classList.add("disabled");
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.setAttribute("disabled", true);
        this.submitButton.textContent = window.cartStrings?.added_to_bundle;
        const formData = new FormData(this.form);
        const variantId = formData.get("id");
        const quantity = formData.get("quantity") || 1;
        const productHandle = this.dataset.handle || this.dataset.productHandle;
        fetch(
          `/products/${productHandle}?section_id=bundle-item&variant=${variantId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = NextSkyTheme.parser.parseFromString(
              responseText,
              "text/html"
            );
            const bundleContainers = this.bundle.querySelectorAll(
              "[data-product-bundle-variant]"
            );
            if (!bundleContainers.length) {
              return;
            }

            let existingItemContainer = null;
            for (const container of bundleContainers) {
              if (container.getAttribute("data-variant-id") === variantId) {
                existingItemContainer = container;
                break;
              }
            }
            if (existingItemContainer) {
            } else {
              let targetContainer = null;
              for (const container of bundleContainers) {
                if (!container.hasAttribute("data-variant-id")) {
                  targetContainer = container;
                  break;
                }
              }
              if (!targetContainer) {
                return;
              }

              const bundleImage = html.querySelector(".bundle-image");
              const bundleContent = html.querySelector(".bundle-content");
              const dataRatio = bundleImage.dataset.ratio;
              const bundleSticky =
                this.closest("build-your-routine").querySelector(
                  ".bundle-sticky"
                );
              if (!bundleImage || !bundleContent) {
                return;
              }
              const enableQuantity =
                bundleSticky.dataset.enableQuantity === "true";
              const bundleQuantity = html.querySelector(".bundle-quantity");
              if (enableQuantity === false) {
                bundleQuantity.remove();
              }
              targetContainer.setAttribute("data-variant-id", variantId);
              targetContainer.setAttribute("data-quantity", quantity);
              targetContainer.setAttribute(
                "data-product-handle",
                productHandle
              );
              const mediaContainer = targetContainer.querySelector(
                "[data-product-bundle-variant-media]"
              );
              if (mediaContainer && bundleImage) {
                mediaContainer.innerHTML = bundleImage.innerHTML;
                mediaContainer.style.setProperty("--aspect-ratio", dataRatio);
              }
              const contentContainer = targetContainer.querySelector(
                "[data-product-bundle-variant-content]"
              );
              if (contentContainer && bundleContent) {
                contentContainer
                  .querySelectorAll(".skeleton-product__info")
                  .forEach((el) => el.remove());
                contentContainer.innerHTML = bundleContent.innerHTML;
              }
            }
          })
          .catch((error) => {
            console.error("Error adding product to bundle:", error);
          })
          .finally(() => {
            new LazyLoader(".image-lazy-load");
            // Utiliser setTimeout pour s'assurer que le DOM est complètement mis à jour
            setTimeout(() => {
              this.updateBundleButtonStatus();
              this.updateDiscount();
              this.updateBundleTotal();
              this.updateProductFormButtons();
              this.saveBundleToStorage();
              document.dispatchEvent(new CustomEvent("bundle:item-changed"));
            }, 50);
          });
      }

      updateBundleButtonStatus() {
        const minimum = parseInt(this.dataset.minimum) || 0;
        const maximum = parseInt(this.dataset.maximum) || 999;
        const submitButton = this.bundle.querySelector(
          `.button-submit-bundle-${this.sectionId}`
        );
        if (!submitButton) return;

        const bundleItems = this.bundle.querySelectorAll(
          "[data-product-bundle-variant][data-variant-id]"
        );

        // Compter soit les produits uniques, soit les quantités totales
        const countQuantities = this.bundle.dataset.countQuantities === "true";
        let itemCount = 0;
        if (countQuantities) {
          bundleItems.forEach((item) => {
            const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
            itemCount += quantity;
          });
        } else {
          itemCount = bundleItems.length;
        }

        if (itemCount >= minimum) {
          submitButton.classList.remove("disabled");
          submitButton.removeAttribute("aria-disabled");
          submitButton.removeAttribute("disabled");
          submitButton.setAttribute("tabindex", "0");
        } else {
          submitButton.classList.add("disabled");
          submitButton.setAttribute("aria-disabled", true);
          submitButton.setAttribute("disabled", true);
          submitButton.removeAttribute("tabindex");
        }

        const buttonAddBundle = this.bundle.querySelectorAll(
          "product-form-bundle button"
        );

        // Compter soit les produits uniques, soit les quantités totales pour le maximum
        // Réutiliser countQuantities déjà déclaré plus haut
        let itemCountForMax = 0;
        if (countQuantities) {
          bundleItems.forEach((item) => {
            const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
            itemCountForMax += quantity;
          });
        } else {
          itemCountForMax = bundleItems.length;
        }

        if (itemCountForMax >= maximum) {
          buttonAddBundle.forEach((button) => {
            button.classList.add("disabled");
            button.setAttribute("disabled", true);
            button.setAttribute("aria-disabled", true);
          });
        }
      }

      updateBundleTotal() {
        BundleUtils.setBundleTotal(this.sectionId, this.totalPrice);
        // Mettre à jour l'affichage de la remise dans le bloc "composer votre pack"
        const activeDiscountTitle = this.bundle.dataset.activeDiscountTitle;
        const activeDiscountAmount = this.bundle.dataset.activeDiscountAmount;
        if (activeDiscountTitle && activeDiscountAmount) {
          const discountAmount = parseInt(activeDiscountAmount) || 0;
          BundleUtils.setBundleDiscountInfo(this.sectionId, {
            title: `${activeDiscountTitle} (-${NextSkyTheme.formatMoney(
              discountAmount,
              themeGlobalVariables.settings.money_format
            )})`,
          });
        } else {
          BundleUtils.setBundleDiscountInfo(this.sectionId, null);
        }
      }

      get totalPrice() {
        return BundleUtils.computeTotalPrice(this.bundle, this.dataset);
      }

      get totalPriceNotDiscount() {
        let itemTotalPrice = 0;
        const bundleItems = this.bundle.querySelectorAll(
          "[data-product-bundle-variant][data-variant-id]"
        );
        bundleItems.forEach((item) => {
          const priceElement = item.querySelector(".product__price");
          const price = parseFloat(priceElement?.dataset.price || 0);
          const quantity = parseInt(item.getAttribute("data-quantity")) || 1;

          if (price) {
            itemTotalPrice += price * quantity;
          }
        });

        return itemTotalPrice;
      }

      updateDiscount() {
        const bundleItems = this.bundle.querySelectorAll(
          "[data-product-bundle-variant][data-variant-id]"
        );

        // Compter soit les produits uniques, soit les quantités totales
        const countQuantities = this.bundle.dataset.countQuantities === "true";
        let itemCount = 0;
        if (countQuantities) {
          bundleItems.forEach((item) => {
            const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
            itemCount += quantity;
          });
        } else {
          itemCount = bundleItems.length;
        }

        // Récupérer les données de remise conditionnelle
        // Note: Les attributs data-* avec tirets sont convertis en camelCase dans dataset
        // data-enable-discount-1 devient dataset.enableDiscount1
        // data-discount-title-1 devient dataset.discountTitle1
        // data-discount-value-1 devient dataset.discountValue1
        // data-discount-minimum-1 devient dataset.discountMinimum1

        // Debug: Afficher TOUS les attributs data disponibles

        // Les attributs data-* avec chiffres à la fin ne sont pas correctement convertis en camelCase
        // On doit utiliser getAttribute() ou la notation avec tiret dans dataset
        const enableDiscount1 =
          this.bundle.getAttribute("data-enable-discount-1") === "true";
        const discountTitle1 =
          this.bundle.getAttribute("data-discount-title-1") || "";
        const discountType1 =
          this.bundle.getAttribute("data-discount-type-1") || "percentage";
        const discountValue1 = enableDiscount1
          ? Number(this.bundle.getAttribute("data-discount-value-1")) || 0
          : 0;
        const discountMinimum1 = enableDiscount1
          ? parseInt(this.bundle.getAttribute("data-discount-minimum-1")) || 0
          : 0;

        const enableDiscount2 =
          this.bundle.getAttribute("data-enable-discount-2") === "true";
        const discountTitle2 =
          this.bundle.getAttribute("data-discount-title-2") || "";
        const discountType2 =
          this.bundle.getAttribute("data-discount-type-2") || "percentage";
        const discountValue2 = enableDiscount2
          ? Number(this.bundle.getAttribute("data-discount-value-2")) || 0
          : 0;
        const discountMinimum2 = enableDiscount2
          ? parseInt(this.bundle.getAttribute("data-discount-minimum-2")) || 0
          : 0;

        // Récupérer les données de remise existante (fallback)
        const minimum = parseInt(this.bundle.dataset.minimum) || 0;
        const discountTitle = this.bundle.dataset.discountTitle;
        const discountValue = this.bundle.dataset.discountValue;
        const discountValuePercentage = Number(
          this.bundle.dataset.valuePercentage
        );
        const discountValueFixedAmount = Number(
          this.bundle.dataset.valueFixedAmount
        );
        const dataOncePerOrder = this.bundle.dataset.oncePerOrder === "true";

        // Déterminer quelle remise appliquer
        let activeDiscount = null;

        // Si les remises conditionnelles sont activées, elles ont la priorité
        // Vérifier d'abord la remise niveau 2
        if (
          enableDiscount2 &&
          discountTitle2 &&
          itemCount >= discountMinimum2 &&
          discountValue2 > 0
        ) {
          activeDiscount = {
            title: discountTitle2,
            type: discountType2,
            value: discountValue2,
          };
        }
        // Sinon vérifier la remise niveau 1
        else if (
          enableDiscount1 &&
          discountTitle1 &&
          itemCount >= discountMinimum1 &&
          discountValue1 > 0
        ) {
          activeDiscount = {
            title: discountTitle1,
            type: discountType1,
            value: discountValue1,
          };
        }
        // Sinon utiliser le système existant (remise par défaut)
        // La remise par défaut s'applique même si les remises conditionnelles sont activées
        // mais que leurs conditions ne sont pas remplies
        else if (discountTitle && itemCount >= minimum) {
          activeDiscount = {
            title: discountTitle,
            type: discountValue,
            value:
              discountValue === "percentage"
                ? discountValuePercentage
                : discountValueFixedAmount,
          };
        } else {
          // Si aucune remise n'est applicable
        }

        const submitButton = this.bundle.querySelector(
          `.button-submit-bundle-${this.sectionId}`
        );
        if (!submitButton) return;

        const bundleDiscount = this.bundle.querySelectorAll(
          "[data-product-bundle-variant][data-variant-id] .discounts"
        );

        // Si aucune remise n'est active, masquer les remises et réinitialiser les prix
        if (!activeDiscount) {
          bundleDiscount.forEach((el) => {
            el.style.display = "none";
          });
          // Réinitialiser les prix si aucune remise n'est active
          bundleItems.forEach((item) => {
            const priceElement = item.querySelector(".product__price");
            if (!priceElement) return;

            // Restaurer le prix original (sans remise)
            const price = parseFloat(priceElement?.dataset.price || 0);
            if (!price) return;

            // Supprimer les éléments de prix avec remise s'ils existent
            const existingPriceContainer = priceElement.querySelector(
              ".card-product-price"
            );
            if (existingPriceContainer) {
              // Vérifier si c'est un prix avec remise (a un prix barré)
              const hasStrikethrough =
                existingPriceContainer.querySelector("s");
              if (hasStrikethrough) {
                // Restaurer le prix simple
                priceElement.innerHTML = `
                  <div class="card-product-price relative">
                    <div class="price-regular">
                      <span class="visually-hidden visually-hidden--inline">
                        ${
                          themeGlobalVariables?.settings?.money_format?.replace(
                            "{{amount}}",
                            ""
                          ) || ""
                        }Prix régulier
                      </span>
                      <span class="price heading-color">
                        ${NextSkyTheme.formatMoney(
                          price,
                          themeGlobalVariables.settings.money_format
                        )}
                      </span>
                    </div>
                  </div>
                `;
              }
            }
          });
          // Masquer aussi la remise dans le bloc "composer votre pack"
          BundleUtils.setBundleDiscountInfo(this.sectionId, null);
          return;
        }

        if (activeDiscount) {
          bundleDiscount.forEach((el) => {
            el.style.display = "block";
          });
          bundleItems.forEach((item) => {
            const priceElement = item.querySelector(".product__price");
            if (!priceElement) return;

            const price = parseFloat(priceElement?.dataset.price || 0);
            if (!price) return;

            const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
            const itemDiscountContent = item.querySelector(
              ".discounts__discount"
            );

            // Calculer la remise
            let discountPerItem = 0;
            let discountTotal = 0;

            if (activeDiscount.type === "percentage") {
              // Calculer la remise en pourcentage (price est déjà en centimes)
              discountPerItem = Math.round(
                (price * activeDiscount.value) / 100
              );
              discountTotal = discountPerItem * quantity;
            } else if (activeDiscount.type === "fixed_amount") {
              if (dataOncePerOrder && discountValue === "fixed_amount") {
                // Utiliser le système existant pour once_per_order
                const totalNotDiscount = this.totalPriceNotDiscount;
                const priceItem = price * quantity;
                discountTotal = Math.round(
                  (priceItem / totalNotDiscount) * (activeDiscount.value * 100)
                );
                discountPerItem = Math.round(discountTotal / quantity);
              } else {
                discountPerItem = activeDiscount.value * 100;
                discountTotal = discountPerItem * quantity;
              }
            }

            // Mettre à jour le texte de remise
            if (itemDiscountContent) {
              itemDiscountContent.textContent = `${
                activeDiscount.title
              } (-${NextSkyTheme.formatMoney(
                discountTotal,
                themeGlobalVariables.settings.money_format
              )})`;
              itemDiscountContent.setAttribute(
                "data-discount-amount",
                discountTotal
              );
            }

            // Calculer le prix avec remise
            const originPriceSale = price - discountPerItem;

            // Créer les nouveaux éléments de prix
            const priceContainer = document.createElement("div");
            priceContainer.classList.add(
              "card-product-price",
              "relative",
              "flex",
              "align-center",
              "wrap",
              "gap-2",
              "row-gap-0"
            );

            const salePriceElement = document.createElement("div");
            salePriceElement.classList.add(
              "heading-color",
              "price-regular",
              "inline-block"
            );

            const priceSale = document.createElement("span");
            priceSale.classList.add(
              "price",
              "special-price",
              "heading-style",
              "sale-color"
            );
            priceSale.textContent = NextSkyTheme.formatMoney(
              originPriceSale,
              themeGlobalVariables.settings.money_format
            );
            salePriceElement.appendChild(priceSale);

            const priceRegular = document.createElement("div");
            priceRegular.classList.add(
              "price-regular",
              "grey-color",
              "inline-block"
            );

            const priceOriginal = document.createElement("s");
            priceOriginal.classList.add(
              "price-item",
              "compare-price",
              "body_weight",
              "text-size"
            );
            priceOriginal.textContent = NextSkyTheme.formatMoney(
              price,
              themeGlobalVariables.settings.money_format
            );
            priceRegular.appendChild(priceOriginal);

            priceContainer.appendChild(salePriceElement);
            priceContainer.appendChild(priceRegular);

            // Remplacer complètement le contenu pour éviter les doublons
            priceElement.innerHTML = "";
            priceElement.appendChild(priceContainer);
          });

          // Mettre à jour l'affichage de la remise dans le bloc "composer votre pack"
          // Convertir bundleItems en tableau si c'est un NodeList
          const bundleItemsArray = Array.from(bundleItems);
          const discountAmount = bundleItemsArray.reduce((total, item) => {
            const priceElement = item.querySelector(".product__price");
            if (!priceElement) return total;
            const price = parseFloat(priceElement?.dataset.price || 0);
            if (!price) return total;
            const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
            let itemDiscount = 0;
            if (activeDiscount.type === "percentage") {
              itemDiscount =
                Math.round((price * activeDiscount.value) / 100) * quantity;
            } else {
              itemDiscount = activeDiscount.value * 100 * quantity;
            }
            return total + itemDiscount;
          }, 0);

          BundleUtils.setBundleDiscountInfo(this.sectionId, {
            title: `${activeDiscount.title} (-${NextSkyTheme.formatMoney(
              discountAmount,
              themeGlobalVariables.settings.money_format
            )})`,
          });
        } else {
          bundleDiscount.forEach((el) => {
            el.style.display = "none";
          });
          // Réinitialiser les prix si aucune remise n'est active
          bundleItems.forEach((item) => {
            const priceElement = item.querySelector(".product__price");
            if (priceElement && priceElement.dataset.price) {
              const price = parseFloat(priceElement.dataset.price || 0);
              // Vérifier si le prix a été modifié (contient un prix barré)
              const existingPriceContainer = priceElement.querySelector(
                ".card-product-price"
              );
              if (existingPriceContainer) {
                // Restaurer le prix simple sans remise
                priceElement.innerHTML = `
                  <div class="card-product-price relative">
                    <div class="price-regular">
                      <span class="visually-hidden visually-hidden--inline">
                        Prix régulier
                      </span>
                      <span class="price heading-color">
                        ${NextSkyTheme.formatMoney(
                          price,
                          themeGlobalVariables.settings.money_format
                        )}
                      </span>
                    </div>
                  </div>
                `;
              }
            }
          });
          // Masquer aussi la remise dans le bloc "composer votre pack"
          BundleUtils.setBundleDiscountInfo(this.sectionId, null);
        }
      }

      loadBundleFromStorage() {
        try {
          const savedItems = localStorage.getItem(this.localStorageKey);
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            const cartDrawerId = cartDrawer.getAttribute("id");
            this.bundleSubmitButton.setAttribute(
              "data-cart-drawer-id",
              cartDrawerId
            );
          }
          const discountCode = this.dataset.discountTitle;
          if (discountCode) {
            const existingDiscounts = localStorage.getItem(
              this.localDiscountKey
            );
            let discountCodes = [];
            if (existingDiscounts) {
              try {
                discountCodes = JSON.parse(existingDiscounts);
                if (!Array.isArray(discountCodes)) {
                  discountCodes = [existingDiscounts];
                }
              } catch (e) {
                discountCodes = [existingDiscounts];
              }
            }

            if (!discountCodes.includes(discountCode)) {
              discountCodes.push(discountCode);
              localStorage.setItem(
                this.localDiscountKey,
                JSON.stringify(discountCodes)
              );
            }
          }
          const existingSectionId = localStorage.getItem(this.localSectionKey);
          let sectionIds = [];
          if (existingSectionId) {
            try {
              sectionIds = JSON.parse(existingSectionId);
              if (!Array.isArray(sectionIds)) {
                sectionIds = [existingSectionId];
              }
            } catch (e) {
              sectionIds = [existingSectionId];
            }
          }

          if (!sectionIds.includes(this.sectionId)) {
            sectionIds.push(this.sectionId);
            localStorage.setItem(
              this.localSectionKey,
              JSON.stringify(sectionIds)
            );
          }
          if (savedItems) {
            const bundleItems = JSON.parse(savedItems);

            if (this.isLoadingFromStorage) return;
            this.isLoadingFromStorage = true;

            if (!Array.isArray(bundleItems) || bundleItems.length === 0) {
              this.isLoadingFromStorage = false;
              return;
            }

            const validItems = bundleItems.filter(
              (item) =>
                item &&
                item.variantId &&
                item.productHandle &&
                item.quantity &&
                typeof item.quantity === "number"
            );

            if (validItems.length === 0) {
              this.isLoadingFromStorage = false;
              return;
            }

            this.clearExistingBundleItems();

            this.loadAllItemsParallel(validItems);
          }
          const minMaxData = {
            minimum: this.minimum,
            maximum: this.maximum,
          };
          localStorage.setItem(this.localMinMaxKey, JSON.stringify(minMaxData));
        } catch (error) {
          console.error("Error loading bundle from storage:", error);
          this.isLoadingFromStorage = false;
        }
      }

      loadAllItemsParallel(items) {
        const fetchPromises = items.map((item) => this.fetchProductData(item));

        Promise.all(fetchPromises)
          .then((results) => {
            const validResults = results.filter((result) => result !== null);

            validResults.forEach((result, index) => {
              this.applyProductToContainer(result, index);
            });

            this.isLoadingFromStorage = false;
            setTimeout(() => {
              new LazyLoader(".image-lazy-load");
              this.updateBundleButtonStatus();
              this.updateDiscount();
              this.updateBundleTotal();
              this.updateProductFormButtons();
              document.dispatchEvent(new CustomEvent("bundle:item-changed"));
            }, 50);
          })
          .catch((error) => {
            console.error("Error loading bundle items:", error);
            this.isLoadingFromStorage = false;
          });
      }

      fetchProductData(item) {
        const { variantId, quantity, productHandle } = item;

        return fetch(
          `/products/${productHandle}?section_id=bundle-item&variant=${variantId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = NextSkyTheme.parser.parseFromString(
              responseText,
              "text/html"
            );
            const bundleImage = html.querySelector(".bundle-image");
            const bundleContent = html.querySelector(".bundle-content");

            if (!bundleImage || !bundleContent) {
              console.warn(
                "Bundle image or content not found for variant:",
                variantId
              );
              return null;
            }

            return {
              variantId,
              quantity,
              productHandle,
              bundleImage,
              bundleContent,
              dataRatio: bundleImage.dataset.ratio,
            };
          })
          .catch((error) => {
            console.error("Error fetching product data:", error);
            return null;
          });
      }

      applyProductToContainer(productData, index) {
        const {
          variantId,
          quantity,
          productHandle,
          bundleImage,
          bundleContent,
          dataRatio,
        } = productData;

        const bundleContainers = this.bundle.querySelectorAll(
          "[data-product-bundle-variant]"
        );

        const existingContainer = Array.from(bundleContainers).find(
          (container) => container.getAttribute("data-variant-id") === variantId
        );

        if (existingContainer) {
          const currentQuantity =
            parseInt(existingContainer.getAttribute("data-quantity")) || 1;
          if (currentQuantity !== quantity) {
            existingContainer.setAttribute("data-quantity", quantity);
            const quantityInput = existingContainer.querySelector(
              "quantity-input-bundle input"
            );
            if (quantityInput) {
              quantityInput.value = quantity;
            }
          }
          return;
        }

        let targetContainer = null;
        for (const container of bundleContainers) {
          if (!container.hasAttribute("data-variant-id")) {
            targetContainer = container;
            break;
          }
        }

        if (!targetContainer) {
          console.warn("No empty container available for variant:", variantId);
          return;
        }

        const bundleSticky =
          this.closest("build-your-routine").querySelector(".bundle-sticky");
        const enableQuantity = bundleSticky.dataset.enableQuantity === "true";

        const clonedContent = bundleContent.cloneNode(true);
        const bundleQuantity = clonedContent.querySelector(".bundle-quantity");
        if (enableQuantity === false && bundleQuantity) {
          bundleQuantity.remove();
        }

        targetContainer.setAttribute("data-variant-id", variantId);
        targetContainer.setAttribute("data-quantity", quantity);
        targetContainer.setAttribute("data-product-handle", productHandle);

        const mediaContainer = targetContainer.querySelector(
          "[data-product-bundle-variant-media]"
        );
        if (mediaContainer && bundleImage) {
          mediaContainer.innerHTML = bundleImage.innerHTML;
          mediaContainer.style.setProperty("--aspect-ratio", dataRatio);
        }

        const contentContainer = targetContainer.querySelector(
          "[data-product-bundle-variant-content]"
        );
        if (contentContainer) {
          contentContainer
            .querySelectorAll(".skeleton-product__info")
            .forEach((el) => el.remove());
          contentContainer.innerHTML = clonedContent.innerHTML;
        }

        const quantityInput = targetContainer.querySelector(
          "quantity-input-bundle input"
        );
        if (quantityInput) {
          quantityInput.value = quantity;
        }
      }

      clearExistingBundleItems() {
        const bundleContainers = this.bundle.querySelectorAll(
          "[data-product-bundle-variant][data-variant-id]"
        );
        bundleContainers.forEach((container) =>
          BundleUtils.clearContainer(container)
        );
      }

      restoreProductToBundle(item) {
        const { variantId, quantity, productHandle } = item;

        return fetch(
          `/products/${productHandle}?section_id=bundle-item&variant=${variantId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = NextSkyTheme.parser.parseFromString(
              responseText,
              "text/html"
            );
            const bundleContainers = this.bundle.querySelectorAll(
              "[data-product-bundle-variant]"
            );

            if (!bundleContainers.length) return;

            const existingContainer = Array.from(bundleContainers).find(
              (container) =>
                container.getAttribute("data-variant-id") === variantId
            );

            if (existingContainer) {
              const currentQuantity =
                parseInt(existingContainer.getAttribute("data-quantity")) || 1;
              if (currentQuantity !== quantity) {
                existingContainer.setAttribute("data-quantity", quantity);
                const quantityInput = existingContainer.querySelector(
                  "quantity-input-bundle input"
                );
                if (quantityInput) {
                  quantityInput.value = quantity;
                }
              }
              return;
            }

            let targetContainer = null;
            for (const container of bundleContainers) {
              if (!container.hasAttribute("data-variant-id")) {
                targetContainer = container;
                break;
              }
            }

            if (!targetContainer) {
              console.warn(
                "No empty container available for variant:",
                variantId
              );
              return;
            }

            const bundleImage = html.querySelector(".bundle-image");
            const bundleContent = html.querySelector(".bundle-content");

            if (!bundleImage || !bundleContent) {
              console.warn(
                "Bundle image or content not found for variant:",
                variantId
              );
              return;
            }

            const dataRatio = bundleImage.dataset.ratio;
            const bundleSticky =
              this.closest("build-your-routine").querySelector(
                ".bundle-sticky"
              );

            const enableQuantity =
              bundleSticky.dataset.enableQuantity === "true";
            const bundleQuantity = html.querySelector(".bundle-quantity");
            if (enableQuantity === false && bundleQuantity) {
              bundleQuantity.remove();
            }

            targetContainer.setAttribute("data-variant-id", variantId);
            targetContainer.setAttribute("data-quantity", quantity);
            targetContainer.setAttribute("data-product-handle", productHandle);

            const mediaContainer = targetContainer.querySelector(
              "[data-product-bundle-variant-media]"
            );
            if (mediaContainer && bundleImage) {
              mediaContainer.innerHTML = bundleImage.innerHTML;
              mediaContainer.style.setProperty("--aspect-ratio", dataRatio);
            }

            const contentContainer = targetContainer.querySelector(
              "[data-product-bundle-variant-content]"
            );
            if (contentContainer && bundleContent) {
              contentContainer
                .querySelectorAll(".skeleton-product__info")
                .forEach((el) => el.remove());
              contentContainer.innerHTML = bundleContent.innerHTML;
            }

            const quantityInput = targetContainer.querySelector(
              "quantity-input-bundle input"
            );
            if (quantityInput) {
              quantityInput.value = quantity;
            }

            new LazyLoader(".image-lazy-load");
            document.dispatchEvent(new CustomEvent("bundle:item-changed"));
          })
          .catch((error) => {
            console.error("Error restoring product to bundle:", error);
            throw error;
          });
      }

      saveBundleToStorage() {
        BundleUtils.saveBundleToStorage(this.bundle, this.sectionId);
      }

      updateProductFormButtons() {
        const bundleItems = BundleUtils.getBundleItems(this.bundle);
        const maximum = parseInt(this.dataset.maximum) || 999;
        const shouldDisableAllButtons = bundleItems.length >= maximum;
        BundleUtils.updateProductFormButtons(this.bundle, {
          disableAll: shouldDisableAllButtons,
        });
      }
    }
  );
}
class ButtonSubmitBundle extends HTMLElement {
  constructor() {
    super();
    this.submitButton = this;
    this.sectionId = this.dataset.sectionId;
    this.addEventListener("click", this.onSubmitHandler.bind(this));
    this.setAttribute("aria-disabled", true);
    this.setAttribute("disabled", true);
    this.cart =
      document.querySelector("cart-drawer") ||
      document.querySelector("main-cart");
    this.wrapper = this.closest("build-your-routine");
    this.minimum = parseInt(this.wrapper?.dataset.minimum) || 0;
    // Le code de réduction sera déterminé dynamiquement selon les remises conditionnelles
    this.discountCode = this.dataset.discountCode || "";
    this.localStorageKey = `bundle-items-${this.sectionId}`;
    this.addEventListener("keydown", this.handleKeyDown.bind(this));

    // Écouter les changements de quantité pour mettre à jour le statut du bouton
    document.addEventListener("bundle:quantity-changed", () => {
      this.updateButtonStatus();
    });
    document.addEventListener("bundle:item-changed", () => {
      this.updateButtonStatus();
    });

    // Mettre à jour le statut du bouton à l'initialisation
    // (au cas où des produits seraient déjà chargés depuis le localStorage)
    setTimeout(() => {
      this.updateButtonStatus();
    }, 100);
  }

  getActiveDiscountCode() {
    // Vérifier d'abord le code stocké par computeTotalPrice (remises conditionnelles)
    if (this.wrapper.dataset.activeDiscountCode) {
      return this.wrapper.dataset.activeDiscountCode;
    }
    // Sinon utiliser le code de réduction existant
    return this.discountCode || "";
  }

  handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.onSubmitHandler(event);
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;
    this.submitButton.setAttribute("aria-disabled", true);
    this.submitButton.classList.add("loading");
    const bundleItems = this.wrapper.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id]"
    );
    const items = [];
    bundleItems.forEach((item) => {
      const variantId = item.getAttribute("data-variant-id");
      const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
      // Vérifier que variantId existe et est valide
      if (variantId && !isNaN(parseInt(variantId))) {
        items.push({ id: parseInt(variantId), quantity: quantity });
      }
    });

    // Vérifier qu'il y a au moins un item
    if (items.length === 0) {
      console.error("Aucun produit valide à ajouter au panier");
      this.submitButton.classList.remove("loading");
      this.submitButton.removeAttribute("aria-disabled");
      return;
    }

    this.addItemsToCart(items);
  }

  addItemsToCart(items) {
    // Vérifier que les items sont valides
    if (!items || items.length === 0) {
      this.submitButton.classList.remove("loading");
      this.submitButton.removeAttribute("aria-disabled");
      return;
    }

    // Filtrer et valider les items
    const validItems = items
      .map((item) => {
        const variantId = parseInt(item.id);
        const quantity = parseInt(item.quantity) || 1;
        if (!isNaN(variantId) && variantId > 0 && quantity > 0) {
          return { id: variantId, quantity: quantity };
        }
        return null;
      })
      .filter((item) => item !== null);

    if (validItems.length === 0) {
      this.submitButton.classList.remove("loading");
      this.submitButton.removeAttribute("aria-disabled");
      return;
    }

    // Utiliser /cart/add.js avec JSON pour ajouter les items (comme dans compact-product-bundle)
    // Puis récupérer les sections séparément
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items: validItems }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            const errorMessage =
              errorData.description ||
              errorData.message ||
              "Erreur lors de l'ajout au panier";
            notifier.show(errorMessage, "error", 4000);
            this.error = true;
            throw new Error(errorMessage);
          });
        }
        return response.json();
      })
      .then((response) => {
        // /cart/add.js retourne les items ajoutés ou une erreur
        // Vérifier si c'est une erreur (Shopify retourne {status: 422, description: "...", message: "..."})
        if (response.status) {
          const errorMessage =
            response.description ||
            response.message ||
            "Erreur lors de l'ajout au panier";
          notifier.show(errorMessage, "error", 4000);
          this.error = true;
          return;
        }

        // Succès - les items ont été ajoutés
        // Publier l'événement cartUpdate pour mettre à jour le drawer et l'icône du panier
        if (!this.error) {
          NextSkyTheme.publish(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, {
            source: "product-bundle",
            cartData: response,
          });
        }
        this.error = false;

        const is_cart_page = document.body.classList.contains("template-cart");

        // Si on est sur la page panier, recharger la page
        if (is_cart_page) {
          window.location = window.routes.cart_url;
          return;
        }

        // Si pas de drawer ou action = page, rediriger vers le panier
        if (!this.cart || this.addCartType == "page") {
          const activeDiscountCode = this.getActiveDiscountCode();
          if (activeDiscountCode) {
            this.applyDiscount();
          } else {
            window.location = window.routes.cart_url;
          }
          return;
        }

        // Mettre à jour le drawer
        const activeDiscountCode = this.getActiveDiscountCode();

        if (activeDiscountCode) {
          // Appliquer la remise et mettre à jour le drawer
          this.applyDiscount(true);
        } else {
          // Récupérer les sections et mettre à jour le drawer
          if (this.cart && this.cart.getSectionsToRender) {
            const sectionsToRender = this.cart
              .getSectionsToRender()
              .map((section) => section.id);

            if (sectionsToRender.length > 0) {
              const sectionsUrl = `${
                routes?.cart_url
              }?sections=${sectionsToRender.join(
                ","
              )}&sections_url=${encodeURIComponent(window.location.pathname)}`;

              fetch(sectionsUrl)
                .then((response) => {
                  return response.text();
                })
                .then((html) => {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(html, "text/html");
                  const sectionsData = {};
                  sectionsToRender.forEach((sectionId) => {
                    const sectionElement = doc.querySelector(
                      `#shopify-section-${sectionId}`
                    );
                    if (sectionElement) {
                      sectionsData[sectionId] = sectionElement.innerHTML;
                    }
                  });

                  // Mettre à jour le drawer avec les sections récupérées
                  if (Object.keys(sectionsData).length > 0) {
                    try {
                      this.cart.renderContents({ sections: sectionsData });
                    } catch (e) {
                      console.error("Erreur renderContents():", e);
                    }
                  }

                  // Ouvrir le drawer après un délai
                  setTimeout(() => {
                    this.openCartDrawer();
                  }, 300);
                })
                .catch((e) => {
                  console.error("Erreur fetch sections:", e);
                  // En cas d'erreur, essayer quand même d'ouvrir le drawer
                  if (this.cart && this.cart.renderContents) {
                    try {
                      this.cart.renderContents();
                    } catch (e) {
                      console.error("Erreur renderContents() fallback:", e);
                    }
                  }
                  setTimeout(() => {
                    this.openCartDrawer();
                  }, 300);
                });
            } else {
              setTimeout(() => {
                this.openCartDrawer();
              }, 100);
            }
          } else if (this.cart) {
            setTimeout(() => {
              this.openCartDrawer();
            }, 100);
          } else {
            window.location = window.routes.cart_url;
          }
        }
      })
      .catch((e) => {
        console.error("Erreur lors de l'ajout au panier:", e);
        notifier.show(
          "Une erreur est survenue lors de l'ajout au panier",
          "error",
          4000
        );
        this.error = true;
      })
      .finally(() => {
        new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
        this.submitButton.classList.remove("loading");
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.updateButtonStatus();
        this.clearBundle();
        this.clearBundleFromStorage();
        this.saveProductIdsToStorage(items);
      });
  }

  openCartDrawer() {
    const actionMobile = themeGlobalVariables?.settings?.actionAddCartMobile;
    let getCookieCartMessage = true;
    if (actionMobile == "cart_message") {
      getCookieCartMessage = NextSkyTheme.getCookie("cart_message");
      if (!getCookieCartMessage) {
        NextSkyTheme.setCookie("cart_message", "true", 1);
      }
    }

    const shouldOpenDrawer =
      window.innerWidth > 767 ||
      actionMobile == "mini_cart" ||
      !getCookieCartMessage;

    if (shouldOpenDrawer) {
      eventModal(this.cart, "open", false, "delay");
    } else {
      notifier.showElement(
        window.cartStrings?.addSuccessMobile || "Produits ajoutés au panier",
        null,
        "success",
        2500
      );
    }
  }

  applyDiscount(isModal = false, cartResponse = null) {
    const cartDrawerId = this.getAttribute("data-cart-drawer-id");
    const activeDiscountCode = this.getActiveDiscountCode();

    if (!activeDiscountCode) {
      return;
    }
    const existingDiscounts = this.existingDiscounts();
    const sectionsToRender =
      isModal && this.cart && this.cart.getSectionsToRender
        ? this.cart.getSectionsToRender().map((section) => section.id)
        : [];

    const body = JSON.stringify({
      discount: [...existingDiscounts, activeDiscountCode].join(","),
      sections: sectionsToRender,
      sections_url: window.location.pathname,
    });

    fetch(`${routes?.cart_update_url}`, {
      ...NextSkyTheme.fetchConfig(),
      ...{ body },
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        const activeDiscountCode = this.getActiveDiscountCode();
        const discountNotApplicable = response.discount_codes?.find(
          (discount) => {
            return (
              discount.code === activeDiscountCode &&
              discount.applicable === false
            );
          }
        );

        if (discountNotApplicable) {
          // Même si la remise n'est pas applicable, on ouvre le drawer pour montrer les produits ajoutés
          // On continue le flux pour ouvrir le drawer
        }

        if (isModal && this.cart) {
          if (response.sections && Object.keys(response.sections).length > 0) {
            this.cart.renderContents(response);
          } else {
            // Si pas de sections dans la réponse, les récupérer séparément
            if (sectionsToRender.length > 0) {
              const sectionsUrl = `${
                routes?.cart_url
              }?sections=${sectionsToRender.join(
                ","
              )}&sections_url=${encodeURIComponent(window.location.pathname)}`;
              fetch(sectionsUrl)
                .then((response) => response.text())
                .then((html) => {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(html, "text/html");
                  const sectionsData = {};
                  sectionsToRender.forEach((sectionId) => {
                    const sectionElement = doc.querySelector(
                      `#shopify-section-${sectionId}`
                    );
                    if (sectionElement) {
                      sectionsData[sectionId] = sectionElement.innerHTML;
                    }
                  });
                  if (Object.keys(sectionsData).length > 0) {
                    this.cart.renderContents({ sections: sectionsData });
                  }
                  setTimeout(() => {
                    eventModal(this.cart, "open", false, "delay");
                  }, 300);
                })
                .catch((e) => {
                  console.error("Erreur récupération sections:", e);
                  setTimeout(() => {
                    eventModal(this.cart, "open", false, "delay");
                  }, 300);
                });
            } else {
              setTimeout(() => {
                eventModal(this.cart, "open", false, "delay");
              }, 300);
            }
            return;
          }

          setTimeout(() => {
            eventModal(this.cart, "open", false, "delay");
          }, 300);

          if (
            cartDrawerId &&
            response.sections &&
            response.sections[cartDrawerId]
          ) {
            document.addEventListener(
              "modal:opened",
              () => {
                this.renderContent(response.sections[cartDrawerId]);
              },
              { once: true }
            );
          }
        } else {
          window.location = window.routes.cart_url;
        }
      })
      .catch((e) => {
        console.error("Erreur applyDiscount():", e);
      })
      .finally(() => {
        new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
      });
  }

  renderContent(responseHtml) {
    let checkedPurchaseBundle = true;
    const parsedResponse = new DOMParser().parseFromString(
      responseHtml,
      "text/html"
    );
    const responseCodes = parsedResponse.querySelectorAll(
      ".cart-discount__pill"
    );
    responseCodes.forEach((pill) => {
      const code = pill.dataset.discountCode;
      if (code === this.discountCode) {
        checkedPurchaseBundle = false;
      }
    });
    const updateContent = (blockClass) => {
      const source = parsedResponse.querySelector(`.${blockClass}`);
      const destination = this.cart.querySelector(`.${blockClass}`);
      if (source && destination && checkedPurchaseBundle === false) {
        destination.innerHTML = source.innerHTML;
      } else if (source && destination && checkedPurchaseBundle === true) {
        const pill = document.createElement("li");
        pill.classList.add(
          "cart-discount__pill",
          "px-sp-4",
          "bg-secondary",
          "fs-small",
          "rounded-30",
          "inline-flex",
          "content-center",
          "gap-sp-2",
          "py-6",
          "my-5",
          "js-discount"
        );
        pill.setAttribute("data-discount-code", this.discountCode);
        const pillText = document.createElement("p");
        pillText.classList.add("cart-discount__pill-code");
        pillText.textContent = this.discountCode;
        const removeDiscount = document.createElement("remove-discount");
        removeDiscount.classList.add(
          "flex",
          "content-center",
          "loading-smooth",
          "loading_snippet"
        );

        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.classList.add(
          "cart-discount__pill-remove",
          "btn-reset",
          "pointer",
          "flex",
          "content-center",
          "relative"
        );
        removeButton.setAttribute("aria-label", this.discountCode);

        removeButton.innerHTML = `
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" class="icon-zoom will-change text-color hidden-on-load transition-short">
            <use href="#icon-close"></use>
          </svg>
          <span class="icon-rotator absolute top-0 bottom-0 m-auto rounded-50 opacity-0 w-custom h-custom" style="--width: 1.5rem; --height: 1.5rem;"></span>
        `;

        removeDiscount.appendChild(removeButton);

        pill.appendChild(pillText);
        pill.appendChild(removeDiscount);
        destination.appendChild(pill);
      }
    };

    const blocksToUpdate = ["cart-discount__codes"];
    blocksToUpdate.forEach(updateContent);
  }

  existingDiscounts() {
    const discountCodes = [];
    if (!this.cart) {
      return [];
    }
    const discountPills = this.cart.querySelectorAll(".cart-discount__pill");
    if (discountPills) {
      for (const pill of discountPills) {
        if (
          pill instanceof HTMLLIElement &&
          typeof pill.dataset.discountCode === "string"
        ) {
          discountCodes.push(pill.dataset.discountCode);
        }
      }
    }
    return discountCodes;
  }

  clearBundleFromStorage() {
    try {
      localStorage.removeItem(this.localStorageKey);
    } catch (error) {
      console.error("Error clearing bundle from storage:", error);
    }
  }

  clearBundle() {
    document.dispatchEvent(new CustomEvent("bundle:item-changed"));
    const bundleContainers = this.wrapper.querySelectorAll(
      "[data-product-bundle-variant]"
    );
    bundleContainers.forEach((container) =>
      BundleUtils.clearContainer(container)
    );

    BundleUtils.setBundleTotal(this.sectionId, 0);

    this.updateButtonStatus();
    document.dispatchEvent(new CustomEvent("bundle:item-changed"));
  }

  updateButtonStatus() {
    const bundleItems = this.wrapper.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id]"
    );
    const bundleBtnAddCart = this.wrapper.querySelectorAll(
      `product-form-bundle button:not(.btn-sold-out)`
    );

    // Compter soit les produits uniques, soit les quantités totales
    const countQuantities = this.wrapper.dataset.countQuantities === "true";
    let itemCountForMax = 0;
    if (countQuantities) {
      bundleItems.forEach((item) => {
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        itemCountForMax += quantity;
      });
    } else {
      itemCountForMax = bundleItems.length;
    }

    const maximum = parseInt(this.wrapper.dataset.maximum) || 999;
    const shouldDisableButtons = itemCountForMax >= maximum;

    bundleBtnAddCart.forEach((button) => {
      if (!shouldDisableButtons && button.classList.contains("disabled")) {
        button.classList.remove("disabled");
        button.removeAttribute("disabled");
        button.removeAttribute("aria-disabled");
        button.textContent = window.cartStrings?.add_to_bundle;
      }
    });

    let itemCount = 0;
    if (countQuantities) {
      bundleItems.forEach((item) => {
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        itemCount += quantity;
      });
    } else {
      itemCount = bundleItems.length;
    }

    // Récupérer le minimum à utiliser pour activer le bouton
    // Le bouton doit s'activer dès que le minimum du bundle est atteint
    // (pas le minimum de la remise conditionnelle, qui s'applique automatiquement)
    // La remise niveau 1 s'applique automatiquement quand on atteint son minimum,
    // mais le bouton doit être activé dès le minimum du bundle
    let minimum = this.minimum > 0 ? this.minimum : 1;

    if (itemCount >= minimum) {
      this.classList.remove("disabled");
      this.removeAttribute("aria-disabled");
      this.removeAttribute("disabled");
      this.setAttribute("tabindex", "0");
    } else {
      this.classList.add("disabled");
      this.setAttribute("aria-disabled", true);
      this.setAttribute("disabled", true);
      this.removeAttribute("tabindex");
    }

    BundleUtils.updateProductFormButtons(this.wrapper);
  }

  saveProductIdsToStorage(items) {
    try {
      const productStorageKey = `bundle-purchased-products-${this.sectionId}`;

      const newProductIds = items.map((item) => item.id).filter((id) => id);

      if (newProductIds.length === 0) return;

      let existingProductIds = [];
      const existingData = localStorage.getItem(productStorageKey);
      if (existingData) {
        try {
          existingProductIds = JSON.parse(existingData);
          if (!Array.isArray(existingProductIds)) {
            existingProductIds = [];
          }
        } catch (e) {
          console.warn(
            "Error parsing existing product IDs from localStorage:",
            e
          );
          existingProductIds = [];
        }
      }

      newProductIds.forEach((productId) => {
        if (!existingProductIds.includes(productId)) {
          existingProductIds.push(productId);
        }
      });

      localStorage.setItem(
        productStorageKey,
        JSON.stringify(existingProductIds)
      );
    } catch (error) {
      console.error("Error saving product IDs to storage:", error);
    }
  }

  updateProductFormButtons() {}
}
customElements.define("button-submit-bundle", ButtonSubmitBundle);
class BundleCartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.handleRemove.bind(this));
    this.bundle = this.closest("build-your-routine");
    this.variantContainer = this.closest("[data-product-bundle-variant]");
    this.sectionId =
      this.bundle?.dataset.sectionId ||
      this.closest("[data-section-id]")?.dataset.sectionId;
    this.localStorageKey = `bundle-items-${this.sectionId}`;
  }

  connectedCallback() {
    this.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.handleRemove();
      }
    });
  }

  handleRemove() {
    if (!this.variantContainer) return;
    BundleUtils.clearContainer(this.variantContainer);

    if (this.parentNode) {
      this.parentNode.innerHTML = "";
    }
    this.updateContainerOrders();
    this.updateBundleTotal();
    this.updateBundleButtonStatus();
    this.updateDiscount();
    this.updateBundleStorage();

    document.dispatchEvent(
      new CustomEvent("bundle:item-removed", {
        detail: {
          container: this.variantContainer,
        },
      })
    );
    document.dispatchEvent(new CustomEvent("bundle:item-changed"));
  }

  updateBundleStorage() {
    BundleUtils.saveBundleToStorage(this.bundle, this.sectionId);
  }

  updateBundleTotal() {
    const sectionId = this.sectionId || BundleUtils.getSectionId(this.bundle);
    BundleUtils.setBundleTotal(sectionId, this.totalPrice);
  }

  get totalPrice() {
    return BundleUtils.computeTotalPrice(this.bundle, this.bundle.dataset);
  }

  updateBundleButtonStatus() {
    const sectionId =
      this.bundle?.dataset.sectionId ||
      this.closest("[data-section-id]")?.dataset.sectionId;

    if (!sectionId) return;

    const submitButton = document.querySelector(
      `.button-submit-bundle-${sectionId}`
    );

    if (!submitButton) return;

    const minimum =
      this.bundle?.dataset.minimum || submitButton.dataset.minimum || 1;

    const bundleItems = this.bundle.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id]"
    );

    // Compter soit les produits uniques, soit les quantités totales
    const countQuantities = this.bundle?.dataset.countQuantities === "true";
    let itemCount = 0;
    if (countQuantities) {
      bundleItems.forEach((item) => {
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        itemCount += quantity;
      });
    } else {
      itemCount = bundleItems.length;
    }

    if (itemCount >= parseInt(minimum)) {
      submitButton.classList.remove("disabled");
      submitButton.removeAttribute("aria-disabled");
      submitButton.removeAttribute("disabled");
      submitButton.setAttribute("tabindex", "0");
    } else {
      submitButton.classList.add("disabled");
      submitButton.setAttribute("aria-disabled", true);
      submitButton.setAttribute("disabled", true);
      submitButton.removeAttribute("tabindex");
    }

    this.updateProductFormButtons();
  }

  updateDiscount() {
    // Utiliser la même logique que QuantityInputBundle.updateDiscount()
    // Déclencher l'événement pour que ProductForm mette à jour les remises
    document.dispatchEvent(new CustomEvent("bundle:quantity-changed"));
  }

  updateProductFormButtons() {
    BundleUtils.updateProductFormButtons(this.bundle);
  }

  updateContainerOrders() {
    if (!this.bundle) return;

    const bundleContainers = this.bundle.querySelectorAll(
      "[data-product-bundle-variant]"
    );

    bundleContainers.forEach((container) => {
      container.style.order = "";
    });

    let filledCount = 0;
    let emptyCount = 0;

    bundleContainers.forEach((container, index) => {
      if (container.hasAttribute("data-variant-id")) {
        container.style.order = filledCount.toString();
        filledCount++;
      } else {
        container.style.order = (
          bundleContainers.length + emptyCount
        ).toString();
        emptyCount++;
      }

      container.style.transition =
        "transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)";

      if (!container.hasAttribute("data-variant-id")) {
        container.style.transform = "translateY(5px)";
        setTimeout(() => {
          container.style.transform = "translateY(0)";
        }, 50);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleRemove);
  }
}
customElements.define("bundle-cart-remove-button", BundleCartRemoveButton);
class QuantityInputBundle extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');
    this.changeEvent = new Event("change", { bubbles: true });

    this.bundle = this.closest("build-your-routine");
    this.variantContainer = this.closest("[data-product-bundle-variant]");
    this.sectionId =
      this.bundle?.dataset.sectionId ||
      this.closest("[data-section-id]")?.dataset.sectionId;
    this.localStorageKey = `bundle-items-${this.sectionId}`;

    this.minValue = parseInt(this.input.getAttribute("min") || 1);
    this.maxValue = parseInt(this.input.getAttribute("max") || 9999);

    if (!this.input) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.minusButton) {
      this.minusButton.addEventListener("click", this.onMinusClick.bind(this));
    }

    if (this.plusButton) {
      this.plusButton.addEventListener("click", this.onPlusClick.bind(this));
    }

    this.input.addEventListener("change", this.onInputChange.bind(this));

    this.input.addEventListener("keydown", this.onKeyDown.bind(this));

    if (this.minusButton) {
      this.minusButton.setAttribute("aria-label", "Decrease quantity");
      this.minusButton.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.onMinusClick(event);
        }
      });
    }

    if (this.plusButton) {
      this.plusButton.setAttribute("aria-label", "Increase quantity");
      this.plusButton.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.onPlusClick(event);
        }
      });
    }
  }

  onMinusClick(event) {
    event.preventDefault();
    this.updateQuantity(-1);
  }

  onPlusClick(event) {
    event.preventDefault();
    this.updateQuantity(1);
  }

  onInputChange(event) {
    let value = parseInt(this.input.value);

    if (isNaN(value) || value < this.minValue) {
      value = this.minValue;
    } else if (value > this.maxValue) {
      value = this.maxValue;
    }

    this.input.value = value;
    this.updateVariantQuantity(value);

    this.announceChange(value);
  }

  onKeyDown(event) {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.updateQuantity(1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      this.updateQuantity(-1);
    }
  }

  updateQuantity(change) {
    const currentValue = parseInt(this.input.value) || this.minValue;
    let newValue = currentValue + change;

    if (newValue < this.minValue) {
      newValue = this.minValue;
    } else if (newValue > this.maxValue) {
      newValue = this.maxValue;
    }

    if (currentValue === newValue) return;

    this.input.value = newValue;
    this.updateVariantQuantity(newValue);

    this.announceChange(newValue);

    this.input.dispatchEvent(this.changeEvent);
  }

  updateVariantQuantity(quantity) {
    if (!this.variantContainer) return;

    this.variantContainer.setAttribute("data-quantity", quantity);

    this.updateBundleTotal();
    this.updateBundleStorage();
    this.updateDiscount();

    document.dispatchEvent(
      new CustomEvent("bundle:quantity-changed", {
        detail: {
          container: this.variantContainer,
          quantity: quantity,
          variantId: this.variantContainer.getAttribute("data-variant-id"),
        },
      })
    );
  }

  updateBundleStorage() {
    BundleUtils.saveBundleToStorage(this.bundle, this.sectionId);
  }

  updateBundleTotal() {
    const sectionId = this.sectionId || BundleUtils.getSectionId(this.bundle);
    BundleUtils.setBundleTotal(sectionId, this.totalPrice);
    // Mettre à jour l'affichage de la remise dans le bloc "composer votre pack"
    const activeDiscountTitle = this.bundle.dataset.activeDiscountTitle;
    const activeDiscountAmount = this.bundle.dataset.activeDiscountAmount;
    if (activeDiscountTitle && activeDiscountAmount) {
      const discountAmount = parseInt(activeDiscountAmount) || 0;
      BundleUtils.setBundleDiscountInfo(sectionId, {
        title: `${activeDiscountTitle} (-${NextSkyTheme.formatMoney(
          discountAmount,
          themeGlobalVariables.settings.money_format
        )})`,
      });
    } else {
      BundleUtils.setBundleDiscountInfo(sectionId, null);
    }
  }

  get totalPrice() {
    // Utiliser BundleUtils.computeTotalPrice qui gère les remises conditionnelles
    return BundleUtils.computeTotalPrice(this.bundle, this.bundle.dataset);
  }

  updateDiscount() {
    // Trouver le ProductForm associé et utiliser sa méthode updateDiscount
    const productForm = this.bundle?.querySelector("product-form-bundle");
    if (productForm && typeof productForm.updateDiscount === "function") {
      productForm.updateDiscount();
    } else {
      // Sinon déclencher l'événement pour que ProductForm mette à jour les remises
      document.dispatchEvent(new CustomEvent("bundle:quantity-changed"));
    }
  }

  updateDiscount() {
    const bundleItems = this.bundle.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id]"
    );

    // Compter soit les produits uniques, soit les quantités totales
    const countQuantities = this.bundle.dataset.countQuantities === "true";
    let itemCount = 0;
    if (countQuantities) {
      bundleItems.forEach((item) => {
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        itemCount += quantity;
      });
    } else {
      itemCount = bundleItems.length;
    }

    // Récupérer les données de remise conditionnelle
    // Les attributs data-* avec chiffres à la fin ne sont pas correctement convertis en camelCase
    // On doit utiliser getAttribute() directement
    const enableDiscount1 =
      this.bundle.getAttribute("data-enable-discount-1") === "true";
    const discountTitle1 =
      this.bundle.getAttribute("data-discount-title-1") || "";
    const discountType1 =
      this.bundle.getAttribute("data-discount-type-1") || "percentage";
    const discountValue1 = enableDiscount1
      ? Number(this.bundle.getAttribute("data-discount-value-1")) || 0
      : 0;
    const discountMinimum1 = enableDiscount1
      ? parseInt(this.bundle.getAttribute("data-discount-minimum-1")) || 0
      : 0;

    const enableDiscount2 =
      this.bundle.getAttribute("data-enable-discount-2") === "true";
    const discountTitle2 =
      this.bundle.getAttribute("data-discount-title-2") || "";
    const discountType2 =
      this.bundle.getAttribute("data-discount-type-2") || "percentage";
    const discountValue2 = enableDiscount2
      ? Number(this.bundle.getAttribute("data-discount-value-2")) || 0
      : 0;
    const discountMinimum2 = enableDiscount2
      ? parseInt(this.bundle.getAttribute("data-discount-minimum-2")) || 0
      : 0;

    // Récupérer les données de remise existante (fallback)
    const minimum = parseInt(this.bundle.dataset.minimum) || 0;
    const discountTitle = this.bundle.dataset.discountTitle;
    const discountValue = this.bundle.dataset.discountValue;
    const discountValuePercentage = Number(this.bundle.dataset.valuePercentage);
    const discountValueFixedAmount = Number(
      this.bundle.dataset.valueFixedAmount
    );
    const dataOncePerOrder = this.bundle.dataset.oncePerOrder === "true";

    // Déterminer quelle remise appliquer
    let activeDiscount = null;

    // Si les remises conditionnelles sont activées, elles ont la priorité
    // Vérifier d'abord la remise niveau 2
    if (
      enableDiscount2 &&
      discountTitle2 &&
      itemCount >= discountMinimum2 &&
      discountValue2 > 0
    ) {
      activeDiscount = {
        title: discountTitle2,
        type: discountType2,
        value: discountValue2,
      };
    }
    // Sinon vérifier la remise niveau 1
    else if (
      enableDiscount1 &&
      discountTitle1 &&
      itemCount >= discountMinimum1 &&
      discountValue1 > 0
    ) {
      activeDiscount = {
        title: discountTitle1,
        type: discountType1,
        value: discountValue1,
      };
    }
    // Sinon utiliser le système existant (remise par défaut)
    // La remise par défaut s'applique même si les remises conditionnelles sont activées
    // mais que leurs conditions ne sont pas remplies
    else if (discountTitle && itemCount >= minimum) {
      activeDiscount = {
        title: discountTitle,
        type: discountValue,
        value:
          discountValue === "percentage"
            ? discountValuePercentage
            : discountValueFixedAmount,
      };
    }

    const bundleDiscount = this.bundle.querySelectorAll(
      "[data-product-bundle-variant][data-variant-id] .discounts"
    );

    // Si aucune remise n'est active, masquer les remises et réinitialiser les prix
    if (!activeDiscount) {
      bundleDiscount.forEach((el) => {
        el.style.display = "none";
      });
      // Réinitialiser les prix si aucune remise n'est active
      bundleItems.forEach((item) => {
        const priceElement = item.querySelector(".product__price");
        if (priceElement && priceElement.dataset.price) {
          const price = parseFloat(priceElement.dataset.price || 0);
          // Vérifier si le prix a été modifié (contient un prix barré)
          const existingPriceContainer = priceElement.querySelector(
            ".card-product-price"
          );
          if (existingPriceContainer) {
            // Restaurer le prix simple sans remise
            priceElement.innerHTML = `
              <div class="card-product-price relative">
                <div class="price-regular">
                  <span class="visually-hidden visually-hidden--inline">
                    Prix régulier
                  </span>
                  <span class="price heading-color">
                    ${NextSkyTheme.formatMoney(
                      price,
                      themeGlobalVariables.settings.money_format
                    )}
                  </span>
                </div>
              </div>
            `;
          }
        }
      });
      // Masquer aussi la remise dans le bloc "composer votre pack"
      const sectionId = this.sectionId || BundleUtils.getSectionId(this.bundle);
      BundleUtils.setBundleDiscountInfo(sectionId, null);
      return;
    }

    if (activeDiscount) {
      bundleDiscount.forEach((el) => {
        el.style.display = "block";
      });

      bundleItems.forEach((item) => {
        const priceElement = item.querySelector(".product__price");
        if (!priceElement) return;

        const price = parseFloat(priceElement?.dataset.price || 0);
        if (!price) return;

        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        const itemDiscountContent = item.querySelector(".discounts__discount");

        // Calculer la remise
        let discountPerItem = 0;
        let discountTotal = 0;

        if (activeDiscount.type === "percentage") {
          // Le prix est en centimes, calculer la remise en centimes
          discountPerItem = Math.round((price * activeDiscount.value) / 100);
          discountTotal = discountPerItem * quantity;
        } else if (activeDiscount.type === "fixed_amount") {
          if (dataOncePerOrder && discountValue === "fixed_amount") {
            // Utiliser le système existant pour once_per_order
            const priceItem = price * quantity;
            discountTotal = Math.round(
              (priceItem / this.totalPrice) * (activeDiscount.value * 100)
            );
            discountPerItem = Math.round(discountTotal / quantity);
          } else {
            discountPerItem = activeDiscount.value * 100;
            discountTotal = discountPerItem * quantity;
          }
        }

        // Mettre à jour le texte de remise
        if (itemDiscountContent) {
          itemDiscountContent.textContent = `${
            activeDiscount.title
          } (-${NextSkyTheme.formatMoney(
            discountTotal,
            themeGlobalVariables.settings.money_format
          )})`;
        }

        // Mettre à jour les prix barrés si nécessaire
        const existingPriceContainer = priceElement.querySelector(
          ".card-product-price"
        );
        if (existingPriceContainer) {
          const priceSale =
            existingPriceContainer.querySelector(".special-price");
          const priceOriginal =
            existingPriceContainer.querySelector(".compare-price");

          if (priceSale) {
            const originPriceSale = price - discountPerItem;
            priceSale.textContent = NextSkyTheme.formatMoney(
              originPriceSale,
              themeGlobalVariables.settings.money_format
            );
          }

          if (priceOriginal) {
            priceOriginal.textContent = NextSkyTheme.formatMoney(
              price,
              themeGlobalVariables.settings.money_format
            );
          }
        } else if (price > 0) {
          // Créer les éléments de prix si ils n'existent pas
          const originPriceSale = price - discountPerItem;

          const priceContainer = document.createElement("div");
          priceContainer.classList.add(
            "card-product-price",
            "relative",
            "flex",
            "align-center",
            "wrap",
            "gap-2",
            "row-gap-0"
          );

          const salePriceElement = document.createElement("div");
          salePriceElement.classList.add(
            "heading-color",
            "price-regular",
            "inline-block"
          );

          const priceSale = document.createElement("span");
          priceSale.classList.add(
            "price",
            "special-price",
            "heading-style",
            "sale-color"
          );
          priceSale.textContent = NextSkyTheme.formatMoney(
            originPriceSale,
            themeGlobalVariables.settings.money_format
          );
          salePriceElement.appendChild(priceSale);

          const priceRegular = document.createElement("div");
          priceRegular.classList.add(
            "price-regular",
            "grey-color",
            "inline-block"
          );

          const priceOriginal = document.createElement("s");
          priceOriginal.classList.add(
            "price-item",
            "compare-price",
            "body_weight",
            "text-size"
          );
          priceOriginal.textContent = NextSkyTheme.formatMoney(
            price,
            themeGlobalVariables.settings.money_format
          );
          priceRegular.appendChild(priceOriginal);

          priceContainer.appendChild(salePriceElement);
          priceContainer.appendChild(priceRegular);

          priceElement.innerHTML = "";
          priceElement.appendChild(priceContainer);
        }
      });
    } else {
      bundleDiscount.forEach((el) => {
        el.style.display = "none";
      });
      // Réinitialiser les prix si aucune remise n'est active
      bundleItems.forEach((item) => {
        const priceElement = item.querySelector(".product__price");
        if (priceElement && priceElement.dataset.price) {
          const price = parseFloat(priceElement.dataset.price || 0);
          // Vérifier si le prix a été modifié (contient un prix barré)
          if (priceElement.querySelector(".card-product-price")) {
            const originalPriceText = NextSkyTheme.formatMoney(
              price,
              themeGlobalVariables.settings.money_format
            );
            priceElement.innerHTML = originalPriceText;
          }
        }
      });
    }
  }

  announceChange(value) {
    const productTitle =
      this.variantContainer?.querySelector(".product-title")?.textContent ||
      "Product";
    const ariaLive = this.querySelector('[aria-live="polite"]');

    if (!ariaLive) {
      const announcer = document.createElement("div");
      announcer.setAttribute("aria-live", "polite");
      announcer.classList.add("visually-hidden");
      this.appendChild(announcer);

      announcer.textContent = `Quantity for ${productTitle} updated to ${value}`;
    } else {
      ariaLive.textContent = `Quantity for ${productTitle} updated to ${value}`;
    }

    setTimeout(() => {
      if (ariaLive) {
        ariaLive.textContent = "";
      }
    }, 1000);
  }

  disconnectedCallback() {
    if (this.minusButton) {
      this.minusButton.removeEventListener("click", this.onMinusClick);
      this.minusButton.removeEventListener("keydown", this.onKeyDown);
    }

    if (this.plusButton) {
      this.plusButton.removeEventListener("click", this.onPlusClick);
      this.plusButton.removeEventListener("keydown", this.onKeyDown);
    }

    if (this.input) {
      this.input.removeEventListener("change", this.onInputChange);
      this.input.removeEventListener("keydown", this.onKeyDown);
    }
  }
}
customElements.define("quantity-input-bundle", QuantityInputBundle);
class BundleProgressbar extends HTMLElement {
  constructor() {
    super();
    this.minimum = parseInt(this.dataset.minimum) || 1;
    this.updateBundleProgress();

    document.addEventListener("bundle:item-changed", () =>
      this.updateBundleProgress()
    );
    document.addEventListener("bundle:quantity-changed", () =>
      this.updateBundleProgress()
    );
  }

  updateBundleProgress() {
    const bundle = this.closest("build-your-routine");
    if (!bundle) {
      return;
    }

    // Récupérer tous les items qui ont un variant-id (produits ajoutés)
    const bundleItems = Array.from(
      bundle.querySelectorAll("[data-product-bundle-variant][data-variant-id]")
    );

    // Compter soit les produits uniques, soit les quantités totales
    const countQuantities = bundle?.dataset.countQuantities === "true";

    let itemCount = 0;
    if (countQuantities) {
      bundleItems.forEach((item) => {
        const quantity = parseInt(item.getAttribute("data-quantity")) || 1;
        itemCount += quantity;
      });
    } else {
      // Compter les produits uniques (nombre d'éléments avec data-variant-id)
      itemCount = bundleItems.length;
    }

    // Récupérer le minimum à utiliser pour la progress bar
    // La progress bar doit atteindre 100% quand la remise la plus haute est applicable
    // Priorité : remise niveau 2 > remise niveau 1 > remise par défaut

    // Récupérer le minimum du bundle (remise par défaut / niveau 0)
    const minimumFromDataAttr = this.getAttribute("data-minimum");
    const minimumFromBundle = bundle?.dataset.minimum;
    const minimumFromData = this.dataset.minimum;
    const minimumFromThis = this.minimum;

    const defaultMinimum =
      parseInt(minimumFromDataAttr) ||
      parseInt(minimumFromBundle) ||
      parseInt(minimumFromData) ||
      parseInt(minimumFromThis) ||
      1;

    // Vérifier les remises conditionnelles
    const enableDiscount1 =
      bundle?.getAttribute("data-enable-discount-1") === "true";
    const discountMinimum1 = enableDiscount1
      ? parseInt(bundle?.getAttribute("data-discount-minimum-1")) || 0
      : 0;

    const enableDiscount2 =
      bundle?.getAttribute("data-enable-discount-2") === "true";
    const discountMinimum2 = enableDiscount2
      ? parseInt(bundle?.getAttribute("data-discount-minimum-2")) || 0
      : 0;

    // Utiliser le minimum le plus élevé (remise niveau 2 > niveau 1 > par défaut)
    let minimum = defaultMinimum;
    let usedDiscount = "par défaut";

    if (enableDiscount2 && discountMinimum2 > 0) {
      minimum = Math.max(minimum, discountMinimum2);
      usedDiscount = "niveau 2";
    }
    if (enableDiscount1 && discountMinimum1 > 0) {
      minimum = Math.max(minimum, discountMinimum1);
      if (usedDiscount === "par défaut") {
        usedDiscount = "niveau 1";
      }
    }

    let progressPercentage;
    if (itemCount >= minimum) {
      progressPercentage = 100;
    } else {
      progressPercentage = Math.max(0, (itemCount / minimum) * 100);
    }

    this.style.setProperty("--width", `${progressPercentage}%`);

    if (progressPercentage >= 100) {
      this.classList.add("complete");
    } else {
      this.classList.remove("complete");
    }
  }
}
customElements.define("bundle-progress-bar", BundleProgressbar);
class BundleHeader extends HTMLElement {
  constructor() {
    super();
    this._title = this.querySelector(".bundle-title");
    this._content =
      this.closest(".bundle-sticky").querySelector(".bundle-content");
    this.init();
  }

  init() {
    const mediaQuery = window.matchMedia("(max-width: 1024.98px)");
    const handleMediaQueryChange = (mediaQuery) => {
      if (mediaQuery.matches) {
        this.style.pointerEvents = "auto";
        this._content.style.height = 0;
        Motion.press(this._title, (event) => {
          this.onHeaderClicked(event);
        });
      } else {
        this.style.pointerEvents = "none";
        this._content.style.height = "auto";
      }
    };
    handleMediaQueryChange(mediaQuery);
    mediaQuery.addEventListener("change", handleMediaQueryChange);
  }

  onHeaderClicked(event) {
    const transition = { duration: 0.3 };
    let isOpen = event.dataset.isOpen === "true";
    isOpen = !isOpen;
    event.dataset.isOpen = isOpen;
    event.setAttribute("aria-expanded", isOpen);
    const chevron = event.querySelector("svg");
    Motion.animate(chevron, { rotate: isOpen ? 180 : 0 }, { duration: 0.2 });
    Motion.animate(
      this._content,
      isOpen
        ? {
            height: "auto",
          }
        : {
            height: 0,
          },
      transition
    );
  }
}
customElements.define("bundle-header", BundleHeader);
